import type { IconeDeDestino } from "./Icone";
import type { MouseEvent } from "react";

/**
 * O MAPA da navegação — os tipos dos três níveis e as perguntas que se fazem
 * sobre eles.
 *
 * <p>Este arquivo saiu de `Navegacao.tsx`, que tinha <b>804 linhas</b> e era o
 * maior componente do sistema (ADR-0013 §2, teto de 300). O que ele guarda é o
 * que <b>não desenha nada</b>: a forma do mapa e as quatro perguntas que a
 * coluna esticada, a faixa recolhida e a persistência fazem sobre ele.
 *
 * <p>Mora num `.ts` e não num `.tsx` porque é exatamente isso: nenhuma dessas
 * funções devolve elemento. Separá-las é o que permite à faixa e à árvore
 * responderem as mesmas perguntas <b>sem uma copiar a outra</b> — duas cópias
 * divergiriam no dia em que uma aprendesse mais um caso, e a que se cala é a
 * que some com um destino do menu.
 */

/**
 * O terceiro e ÚLTIMO nível — uma operação sobre o módulo que o contém.
 *
 * <p>Não tem `itens` de propósito: é o tipo que impede um quarto nível de
 * nascer sem ninguém decidir por ele.
 */
export interface SubmoduloDeNavegacao {
  id: string;
  rotulo: string;
  /** Caminho da rota. `null` quando a tela ainda não existe. */
  rota: string | null;
  /**
   * O desenho que representa o destino na faixa recolhida.
   *
   * <p>Obrigatório na prática para quem tem `rota`, e há teste guardando
   * isso: na faixa o ícone é a única coisa visível do item, e destino sem
   * desenho ou some do menu ou vira quadrado vazio. Item sem rota não
   * precisa de ícone porque <b>não entra na faixa</b>.
   */
  icone?: IconeDeDestino;
}

export interface ItemDeNavegacao {
  id: string;
  rotulo: string;
  /** Caminho da rota. `null` quando a tela ainda não existe. */
  rota: string | null;
  /** Ver {@link SubmoduloDeNavegacao.icone} — a regra é a mesma nos dois níveis. */
  icone?: IconeDeDestino;
  /**
   * Operações <b>sobre</b> este item. Ausente ou vazio quando o item é folha.
   *
   * <p>Ter filhos é o que transforma a linha em módulo: nome linkado de um
   * lado, botão de disclosure do outro.
   */
  itens?: readonly SubmoduloDeNavegacao[];
}

export interface GrupoDeNavegacao {
  id: string;
  rotulo: string;
  /** Setor da indústria ou peça transversal — muda o agrupamento visual. */
  natureza: "setor" | "transversal";
  itens: ItemDeNavegacao[];
  /** Texto curto quando o grupo inteiro ainda não tem escopo definido. */
  observacao?: string;
}

/**
 * A rota do item cobre a rota atual?
 *
 * <p>Igualdade exata não basta: `/producao/materias-primas/7/etiqueta` é a
 * etiqueta de um lote, e quem está nela está em Produção — o item que leva
 * até lá é "Matérias-primas". Prefixo puro também não serve: `/` é prefixo de
 * tudo e deixaria Assuntos Regulatórios aberto em qualquer tela do sistema.
 */
export function cobre(rota: string, rotaAtual: string): boolean {
  return rotaAtual === rota || (rota !== "/" && rotaAtual.startsWith(`${rota}/`));
}

/**
 * O caminho de um módulo dentro do mapa — `setor/modulo`.
 *
 * <p>É a chave do recolhimento e do `id` do `aria-controls`. Qualificar pelo
 * setor é o que impede dois módulos homônimos em setores diferentes de abrirem
 * e fecharem juntos — e um dia haverá "Fornecedores" em mais de um lugar.
 *
 * <p>A barra é segura porque nenhum id contém barra; há teste guardando isso.
 */
export function caminhoDoModulo(grupo: GrupoDeNavegacao, item: ItemDeNavegacao): string {
  return `${grupo.id}/${item.id}`;
}

/** O item tem filhos? É o que decide entre folha e módulo. */
export function filhosDe(item: ItemDeNavegacao): readonly SubmoduloDeNavegacao[] {
  return item.itens ?? [];
}

/**
 * O caminho, do setor ao módulo, que leva à tela atual. Vazio fora do mapa.
 *
 * <p>Devolve os DOIS níveis quando a tela mora dentro de um módulo: abrir só o
 * setor deixaria a tela atual escondida um recuo mais fundo, que é exatamente
 * o defeito que a regra existe para impedir.
 */
export function caminhoDaRota(
  grupos: readonly GrupoDeNavegacao[],
  rotaAtual: string,
): readonly string[] {
  for (const grupo of grupos) {
    for (const item of grupo.itens) {
      const filhos = filhosDe(item);

      // O filho primeiro: a rota do módulo é PREFIXO da rota dos filhos, e
      // parar no módulo devolveria o caminho pela metade.
      if (filhos.some((filho) => filho.rota !== null && cobre(filho.rota, rotaAtual))) {
        return [grupo.id, caminhoDoModulo(grupo, item)];
      }

      if (item.rota !== null && cobre(item.rota, rotaAtual)) {
        return filhos.length > 0 ? [grupo.id, caminhoDoModulo(grupo, item)] : [grupo.id];
      }
    }
  }
  return [];
}

/**
 * Os destinos de um conjunto de grupos, achatados — <b>e só quem tem destino</b>.
 *
 * <p>Desce aos dois níveis porque um submódulo com rota é destino igual: ele
 * está um recuo mais fundo na lista esticada, não no mapa.
 */
export function destinosDe(
  grupos: readonly GrupoDeNavegacao[],
): readonly { id: string; rotulo: string; rota: string; icone: IconeDeDestino }[] {
  return grupos.flatMap((grupo) =>
    grupo.itens
      .flatMap((item) => [item, ...filhosDe(item)])
      // `icone` também é exigido, e não é preciosismo: sem desenho o item
      // viraria um alvo de 44px em branco. O teste que confronta destino e
      // ícone é o que impede isso de virar item sumido no menu.
      .flatMap((item) =>
        item.rota !== null && item.icone !== undefined
          ? [{
              id: `${grupo.id}/${item.id}`,
              rotulo: item.rotulo,
              rota: item.rota,
              icone: item.icone,
            }]
          : [],
      ),
  );
}

export const temDestino = (grupos: readonly GrupoDeNavegacao[]) => destinosDe(grupos).length > 0;

/**
 * O clique que o roteador atende — e o que ele deixa passar.
 *
 * <p>O `href` real fica no DOM para "abrir em nova aba" e para o leitor de
 * tela; a navegação normal é do roteador. Mora numa função só porque a faixa
 * recolhida navega para os MESMOS destinos: duas cópias divergiriam no dia em
 * que uma delas aprendesse mais um modificador de teclado.
 */
export function interceptar(rota: string, aoNavegar: (rota: string) => void) {
  return (evento: MouseEvent<HTMLAnchorElement>) => {
    if (evento.metaKey || evento.ctrlKey || evento.shiftKey) return;
    evento.preventDefault();
    aoNavegar(rota);
  };
}
